<script setup lang="ts">
/**
 * SYARAT LAYANAN — tema v3.
 *
 * Ketentuan kode aktivasi mengikuti katalog nyata di internal/codes/catalog.go:
 *  - rk-1  : 1 kredit, tanpa masa berlaku
 *  - rk-5  : 5 kredit, tanpa masa berlaku
 *  - bk-3d : akses 3 hari, masa aktif MULAI SAAT REDEEM PERTAMA
 * Jangan mengubah angka di halaman ini tanpa mengubah katalog di backend.
 *
 * Seluruh kalimat dibawa PERSIS dari app/pages/syarat.vue. Canonical menunjuk
 * alamat produksi '/syarat' selama v3 masih prototipe.
 */

const situs = useSitusUrl()
const identitas = useIdentitas()

pakaiSeo({
  judul: 'Syarat Layanan — tools.coreasia.id',
  deskripsi:
    'Ketentuan pemakaian tools.coreasia.id: sifat layanan sebagai alat bantu, aturan kode aktivasi, batas tanggung jawab, dan hukum yang berlaku.',
  path: '/syarat',
  jsonLd: [
    jsonLdRemah(situs, [
      ['Beranda', '/'],
      ['Syarat Layanan', '/syarat']
    ])
  ]
})
</script>

<template>
  <CaHalamanLegal judul="Syarat Layanan" :terakhir="TANGGAL_KEBIJAKAN" ikon="i-lucide-scale">
    <template #pembuka>
      Dengan memakai tools.coreasia.id — termasuk alat gratisnya — kamu dianggap menyetujui
      ketentuan di bawah ini. Bacalah sebelum membayar, terutama bagian 2 dan bagian 6.
    </template>

    <CaCard>
      <h2 class="ca-h3 text-ink">1. Siapa penyedia layanan</h2>
      <CaProsa v-if="identitas.adaNama" class="mt-3">
        <p>
          Layanan ini disediakan oleh <strong>{{ identitas.namaBadanUsaha }}</strong
          ><span v-if="identitas.adaAlamat">, beralamat di {{ identitas.alamat }}</span>. Dalam
          halaman ini, "kami" merujuk pada pihak tersebut.
        </p>
      </CaProsa>
      <CaAlert v-else tone="warn" title="Identitas penyedia belum dicantumkan" class="mt-3">
        Jangan lakukan pembayaran sebelum bagian ini terisi.
      </CaAlert>
    </CaCard>

    <CaCard>
      <h2 class="ca-h3 text-ink">2. Sifat layanan: alat bantu, bukan jaminan diterima</h2>
      <CaProsa class="mt-3">
        <p>
          tools.coreasia.id adalah <strong>alat bantu teknis</strong>. Yang kami kerjakan adalah
          menyesuaikan ukuran, format, dan penamaan berkasmu dengan ketentuan yang kami catat, lalu
          melaporkan apa yang berhasil dan apa yang tidak.
        </p>
        <p>
          Kami <strong>bukan</strong> agen, perwakilan, atau mitra dari instansi mana pun, dan tidak
          berafiliasi dengan penyelenggara seleksi atau pendaftaran mana pun. Kami
          <strong>tidak menjamin</strong> berkasmu akan diterima, tidak menjamin pendaftaranmu
          berhasil, dan tidak menjamin hasil seleksi apa pun. Keputusan itu sepenuhnya ada pada
          verifikator instansi tujuan.
        </p>
        <p>
          Batas ukuran dan format yang kami pakai berasal dari ketentuan yang kami catat pada waktu
          tertentu, lengkap dengan tahun sumbernya. Ketentuan itu bisa berubah sewaktu-waktu tanpa
          pemberitahuan kepada kami. <strong>Kamu tetap wajib memeriksa sendiri</strong> pengumuman
          resmi instansi tujuan sebelum mengunggah.
        </p>
        <p>
          Kebenaran, keaslian, dan masa berlaku isi dokumen — misalnya apakah ijazahmu asli atau
          apakah KTP-mu masih berlaku — sepenuhnya menjadi tanggung jawabmu.
        </p>
      </CaProsa>
    </CaCard>

    <CaCard>
      <h2 class="ca-h3 text-ink">3. Kode aktivasi</h2>
      <CaProsa class="mt-3">
        <ul>
          <li>
            <strong>Tanpa akun.</strong> Setelah pembayaran diterima, kamu mendapat satu kode
            aktivasi. Kode itulah bukti hakmu memakai layanan — tidak ada nama pengguna atau kata
            sandi. Simpan baik-baik; siapa pun yang memegang kodemu dapat memakainya.
          </li>
          <li>
            <strong>Paket berbasis kredit (RapiKontak).</strong> Setiap satu kali proses memotong
            satu kredit. Kredit tidak punya tanggal kedaluwarsa dan boleh dipakai kapan saja.
          </li>
          <li>
            <strong>Paket berbasis masa aktif (BerkasKilat, 3 hari).</strong> Selama masa aktif,
            jumlah pemrosesan tidak dibatasi. Masa aktif mulai dihitung sejak kode
            <strong>pertama kali ditukarkan</strong>, bukan sejak dibayar — jadi kamu boleh membeli
            sekarang dan menukarkannya nanti. Setelah masa aktif habis, kode tidak bisa dipakai lagi
            dan tidak diperpanjang otomatis.
          </li>
          <li>
            <strong>Sesi pemakaian</strong> yang terbentuk setelah menukar kode berumur 7 hari. Bila
            sesi habis sementara kredit atau masa aktifmu masih ada, tukarkan kodemu kembali.
          </li>
          <li>
            <strong>Kredit dan masa aktif tidak dapat diuangkan</strong>, kecuali dalam hal yang
            diatur pada <NuxtLink to="/refund">Kebijakan Pengembalian Dana</NuxtLink>.
          </li>
        </ul>
      </CaProsa>
    </CaCard>

    <CaCard>
      <h2 class="ca-h3 text-ink">4. Harga dan pembayaran</h2>
      <CaProsa class="mt-3">
        <p>
          Harga yang berlaku adalah harga yang ditetapkan server kami saat pesanan dibuat, dan sudah
          final — tidak ada biaya tersembunyi menyusul. Pembayaran dilakukan lewat QRIS melalui
          penyelenggara pembayaran yang kami gunakan. Kode QR punya batas waktu; bila lewat, buat
          pesanan baru. Kami tidak pernah meminta PIN, OTP, nomor kartu, atau kredensial rekeningmu
          — lewat kanal mana pun.
        </p>
      </CaProsa>
    </CaCard>

    <CaCard>
      <h2 class="ca-h3 text-ink">5. Cara pakai yang tidak diperbolehkan</h2>
      <CaProsa class="mt-3">
        <ul>
          <li>
            Mengunggah dokumen palsu, hasil pemalsuan, atau dokumen milik orang lain tanpa izin yang
            bersangkutan.
          </li>
          <li>
            Mengunggah materi yang melanggar hukum, atau memakai layanan ini untuk kegiatan yang
            melanggar hukum.
          </li>
          <li>
            Mengakali batas permintaan, memakai skrip otomatis untuk membanjiri layanan, atau
            berusaha menembus pembatasan teknis.
          </li>
          <li>Menjual kembali atau membagikan akses berbayar di luar pemakaian pribadimu sendiri.</li>
        </ul>
        <p>
          Bila ketentuan ini dilanggar, kami dapat menghentikan akses kode terkait tanpa
          pengembalian dana.
        </p>
      </CaProsa>
    </CaCard>

    <CaCard>
      <h2 class="ca-h3 text-ink">6. Batas tanggung jawab</h2>
      <CaProsa class="mt-3">
        <p>
          Layanan disediakan "sebagaimana adanya". Kami berusaha menjaga layanan tetap tersedia dan
          hasilnya akurat, tetapi tidak menjaminnya bebas gangguan atau bebas kekeliruan.
        </p>
        <p>
          Sepanjang diizinkan hukum yang berlaku, tanggung jawab kami atas kerugian apa pun yang
          timbul dari pemakaian layanan ini <strong>dibatasi paling banyak sebesar jumlah yang kamu
          bayarkan untuk pesanan yang bersangkutan</strong>. Kami tidak bertanggung jawab atas
          kerugian tidak langsung, misalnya kehilangan kesempatan mendaftar, tertolaknya berkas oleh
          verifikator, terlewatnya tenggat pendaftaran, atau kehilangan keuntungan.
        </p>
        <p>
          Ketentuan ini tidak dimaksudkan mengurangi hak-hakmu yang tidak dapat dikesampingkan
          menurut peraturan perundang-undangan Republik Indonesia, termasuk ketentuan perlindungan
          konsumen.
        </p>
      </CaProsa>
    </CaCard>

    <CaCard>
      <h2 class="ca-h3 text-ink">7. Perubahan layanan dan ketentuan</h2>
      <CaProsa class="mt-3">
        <p>
          Kami dapat mengubah, menambah, atau menghentikan fitur, termasuk mengubah harga untuk
          pembelian berikutnya. Kode aktivasi yang sudah kamu bayar tetap berlaku sesuai ketentuan
          saat dibeli. Perubahan halaman ini ditandai dengan tanggal "terakhir diperbarui" di bagian
          atas.
        </p>
      </CaProsa>
    </CaCard>

    <CaCard>
      <h2 class="ca-h3 text-ink">8. Hukum yang berlaku dan penyelesaian sengketa</h2>
      <CaProsa class="mt-3">
        <p>
          Ketentuan ini tunduk pada hukum Republik Indonesia. Bila timbul perselisihan, kedua pihak
          sepakat mengutamakan penyelesaian secara musyawarah lebih dulu dengan menghubungi kami.
          Bila tidak tercapai kesepakatan, perselisihan diselesaikan melalui pengadilan yang
          berwenang di Indonesia.
        </p>
        <p v-if="identitas.adaKontak">Hubungi kami:</p>
      </CaProsa>

      <CaKontak
        class="mt-4"
        konteks="Syarat Layanan"
        pesan="Halo, saya ingin bertanya soal syarat layanan tools.coreasia.id."
        subjek="Pertanyaan syarat layanan — tools.coreasia.id"
      />
      <CaAlert v-if="!identitas.adaKontak" tone="warn" title="Kanal kontak belum tersedia" class="mt-4">
        Kanal kontak resmi belum dicantumkan pada halaman ini.
      </CaAlert>
    </CaCard>

    <template #tautan>
      <NuxtLink to="/privasi" class="text-brand font-semibold underline underline-offset-2">
        Kebijakan Privasi
      </NuxtLink>
      dan
      <NuxtLink to="/refund" class="text-brand font-semibold underline underline-offset-2">
        Kebijakan Pengembalian Dana
      </NuxtLink>.
    </template>
  </CaHalamanLegal>
</template>
